import { Assessment, Client, ConsentForm, Program } from '@haleo-frontend/data-access/models';
import * as moment from 'moment';
import 'moment-timezone';
import { ProgramStatusEnum, ReminderEnum } from '@haleo-frontend/data-access/enums';

export class ReminderUtils {

    static getTherapyConsentReminder(client: Client, program: Program, consentForms: ConsentForm[]) {

        if (consentForms.length === 0) {
            return null;
        }

        if (moment().isAfter(moment(program.startedAt).tz(client.timezone).add(36, 'h'))) {
            return ReminderEnum.TherapyConsentFinalWarning;
        } else if (moment().isAfter(moment(program.startedAt).add(24, 'h'))) {
            return ReminderEnum.TherapyConsentFirstWarning;
        }

        return ReminderEnum.TherapyConsent;
    }

    static getSleepQReminder(client: Client, program: Program, sleepQs: Assessment []) {

        if (sleepQs.length == 0) {
            return null;
        }

        if (moment().isAfter(moment(program.startedAt).tz(client.timezone).add(36, 'h'))) {
            return ReminderEnum.SleepQFinalWarning;
        } else if (moment().isAfter(moment(program.startedAt).add(24, 'h'))) {
            return ReminderEnum.SleepQFirstWarning;
        } else {
            return ReminderEnum.SleepQ;
        }
    }

    static getSleepDiaryReminder(sleepDiary: Assessment | undefined, sleepQs: Assessment []) {

        if (sleepQs.length == 0 && !sleepDiary?.completedAt) {
            return ReminderEnum.SleepDiary;
        }

        return null;
    }

    static getSetupNotificationsReminder(client: Client) {

        if (!client.settings?.notifications?.messages) {
            return ReminderEnum.SetupNotifications;
        }

        return null;
    }

    static getProgramCanceledReminder(program: Program, sleepQs: Assessment []) {

        if (program.status == ProgramStatusEnum.canceled && sleepQs.length > 0) {
            return ReminderEnum.programCanceled;
        }
        return null;
    }

    static getTherapySessionReminder(client: Client, session: any) {

        if (session) {

            const startAt = moment(session.startAt).tz(client.timezone);
            // future session
            if (startAt.isAfter(moment()) &&
                startAt.isBefore(moment().add(24, 'h'))) {
                return ReminderEnum.TherapySession;
            }

            // past session only shows at most 45 min
            if (startAt.isBefore(moment()) &&
                startAt.isAfter(moment().subtract(45, 'minutes'))) {
                return ReminderEnum.TherapySession;
            }
        }

        return null;
    }

    static getPostQReminder(client: Client, postQs: Assessment [], appointment: any) {

        if (postQs.length == 0) {
            return null;
        }

        if (appointment &&
            moment().isBefore(moment(appointment.startAt).tz(client.timezone)) &&
            moment(appointment.startAt).tz(client.timezone).isBefore(moment().add(36, 'h'))) {
            return ReminderEnum.PostQWarning;
        }

        return ReminderEnum.PostQ;
    }

    static getWelcomeMessageKeys(client: Client, program: Program, sleepQs: Assessment [], sleepDiaries: Assessment [], postQs: Assessment [], consentForms: ConsentForm []) {

        let main = '';
        let warning = '';

        const hours = moment().diff(moment(program.startedAt).tz(client.timezone), 'hour');

        if (sleepQs.length > 0 && consentForms.length > 0) {

            if (hours >= 36) {
                main = 'home.welcomeMessage.incompleteSleepQAndConsent.lastWarning.main';
                warning = 'home.welcomeMessage.incompleteSleepQAndConsent.lastWarning.warning';
            } else if (hours >= 24) {
                main = 'home.welcomeMessage.incompleteSleepQAndConsent.firstWarning.main';
            } else {
                main = 'home.welcomeMessage.default';
            }

        } else if (sleepQs.length > 0) {

            if (hours >= 36) {
                main = 'home.welcomeMessage.incompleteSleepQ.lastWarning.main';
                warning = 'home.welcomeMessage.incompleteSleepQAndConsent.lastWarning.warning';
            } else if (hours >= 24) {
                main = 'home.welcomeMessage.incompleteSleepQ.firstWarning.main';
            } else {
                main = 'home.welcomeMessage.default';
            }

        } else if (consentForms.length > 0) {

            if (hours >= 36) {
                main += 'home.welcomeMessage.incompleteConsent.lastWarning.main';
                warning = '';
            } else if (hours >= 24) {
                main += 'home.welcomeMessage.incompleteConsent.firstWarning.main';
            } else {
                main += 'home.welcomeMessage.default';
            }

        } else if (sleepQs.length == 0 && sleepDiaries.length == 0) {
            main = 'home.welcomeMessage.initialSleepDiary';
        } else if (postQs.length > 0) {
            main = 'home.welcomeMessage.postQ';
        }

        return {main, warning};
    }


    static configureReminderAppearance(reminder: any) {

        switch (reminder) {
            default:
                return null;
            case ReminderEnum.SleepQ: {
                return {
                    headline: 'home.notification.sleepQReminder.headline',
                    description: 'home.notification.sleepQReminder.description',
                    color: 'info',
                    type: ReminderEnum.SleepQ,
                    priority: 0
                }
            }

            case ReminderEnum.SleepQFirstWarning: {
                return {
                    headline: 'home.notification.sleepQReminder.headline',
                    description: 'home.notification.sleepQReminder.description',
                    color: 'firstWarning',
                    type: ReminderEnum.SleepQFirstWarning,
                    priority: 0
                }
            }

            case ReminderEnum.SleepQFinalWarning: {
                return {
                    headline: 'home.notification.sleepQReminder.headline',
                    description: 'home.notification.sleepQReminder.description',
                    color: 'finalWarning',
                    type: ReminderEnum.SleepQFinalWarning,
                    priority: 0
                }
            }

            case ReminderEnum.TherapyConsent: {
                return {
                    headline: 'home.notification.therapyConsent.headline',
                    description: 'home.notification.therapyConsent.description',
                    color: 'info',
                    type: ReminderEnum.TherapyConsent,
                    priority: 1
                }
            }

            case ReminderEnum.TherapyConsentFirstWarning: {
                return {
                    headline: 'home.notification.therapyConsent.headline',
                    description: 'home.notification.therapyConsent.description',
                    color: 'firstWarning',
                    type: ReminderEnum.TherapyConsentFirstWarning,
                    priority: 1
                }
            }

            case ReminderEnum.TherapyConsentFinalWarning: {
                return {
                    headline: 'home.notification.therapyConsent.headline',
                    description: 'home.notification.therapyConsent.description',
                    color: 'finalWarning',
                    type: ReminderEnum.TherapyConsentFinalWarning,
                    priority: 1
                }
            }

            case ReminderEnum.SleepDiary: {
                return {
                    headline: 'home.notification.sleepDiaryReminder.headline',
                    description: 'home.notification.sleepDiaryReminder.description',
                    color: 'gentle',
                    type: ReminderEnum.SleepDiary,
                    priority: 2
                }
            }

            case ReminderEnum.PostQ: {
                return {
                    headline: 'home.notification.postQ.headline',
                    description: 'home.notification.postQ.description',
                    color: 'info',
                    type: ReminderEnum.PostQ,
                    priority: 2
                }
            }

            case ReminderEnum.PostQWarning: {
                return {
                    headline: 'home.notification.postQ.headline',
                    description: 'home.notification.postQ.description',
                    color: 'firstWarning',
                    type: ReminderEnum.PostQWarning,
                    priority: 2
                }
            }

            case ReminderEnum.TherapySession: {
                return {
                    type: ReminderEnum.TherapySession,
                    priority: 2
                }
            }

            case ReminderEnum.programCanceled: {
                return {
                    type: ReminderEnum.programCanceled,
                    priority: 2
                }
            }

            case ReminderEnum.SetupNotifications: {
                return {
                    type: ReminderEnum.SetupNotifications,
                    priority: 2
                }
            }
        }
    }

    static getReminders(client: Client, program: Program, sleepQs: Assessment [], postQs: Assessment [], consentForms: ConsentForm [], appointment: any, sleepDiary: any) {

        const reminders: (string | null)[] = [];
        if (program?.status === ProgramStatusEnum.ongoing) {
            reminders.push(
                ReminderUtils.getTherapyConsentReminder(client, program, consentForms),
                ReminderUtils.getSleepQReminder(client, program, sleepQs),
                ReminderUtils.getSleepDiaryReminder(sleepDiary, sleepQs)
            )
        }

        reminders.push(
            ReminderUtils.getProgramCanceledReminder(program, sleepQs),
            ReminderUtils.getTherapySessionReminder(client, appointment),
            ReminderUtils.getPostQReminder(client, postQs, appointment),
            ReminderUtils.getSetupNotificationsReminder(client)
        )

        const reminderConfigurations = [];

        for (const type of reminders) {

            const reminder = ReminderUtils.configureReminderAppearance(type)

            if (reminder) {
                reminderConfigurations.push(reminder);
            }
        }

        reminderConfigurations.sort((a, b) => a.priority - b.priority);

        return reminderConfigurations;
    }
}
